import {
  Component,
  inject,
  computed,
  signal,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { ImageViewerService } from '@core/services/image-viewer.service';
import { popDialog, pushDialog } from '../../utils/dialog-a11y.util';

@Component({
  selector: 'app-image-viewer',
  standalone: true,
  imports: [],
  templateUrl: './app-image-viewer.component.html',
  styleUrl: './app-image-viewer.component.css',
})
export class AppImageViewerComponent implements OnInit, OnDestroy {
  private viewerService = inject(ImageViewerService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly stackId = Symbol('image-viewer');

  constructor() {
    // Mientras el visor está abierto es el modal de arriba: Esc y Tab de los diálogos que quedan debajo no actúan.
    effect(() => (this.viewerService.isOpen() ? pushDialog(this.stackId) : popDialog(this.stackId)));
  }

  /**
   * El visor se muestra siempre por encima de cualquier formulario o diálogo. Los diálogos se montan en <body>; si el visor se quedara dentro
   * del layout (que tiene su propio contexto de apilamiento), su z-index no podría superarlos. Por eso se monta también en <body>.
   */
  ngOnInit(): void {
    document.body.appendChild(this.host.nativeElement);
  }

  @ViewChild('imgRef') imgRef!: ElementRef<HTMLImageElement>;

  // ── Estado desde el servicio ─────────────────────────────────────────────
  readonly isOpen = this.viewerService.isOpen;
  readonly config = this.viewerService.config;

  // ── Estado de zoom / paneo ───────────────────────────────────────────────
  scale = signal(1);
  translateX = signal(0);
  translateY = signal(0);

  // ── Paneo con mouse ──────────────────────────────────────────────────────
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private lastTranslateX = 0;
  private lastTranslateY = 0;

  // ── Computed helpers ─────────────────────────────────────────────────────
  readonly canZoomIn = computed(() => this.scale() < 4);
  readonly canZoomOut = computed(() => this.scale() > 0.5);
  readonly isPanned = computed(() => this.translateX() !== 0 || this.translateY() !== 0);
  readonly isZoomed = computed(() => this.scale() !== 1);

  // ── Transformación CSS dinámica ──────────────────────────────────────────
  readonly imgTransform = computed(
    () => `scale(${this.scale()}) translate(${this.translateX() / this.scale()}px, ${this.translateY() / this.scale()}px)`
  );

  // ── Atajos de teclado ────────────────────────────────────────────────────
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) return;
    switch (event.key) {
      case 'Escape': this.close(); break;
      case '+':
      case '=': this.zoomIn(); break;
      case '-': this.zoomOut(); break;
      case '0': this.resetTransform(); break;
    }
  }

  // ── Rueda del ratón para zoom ────────────────────────────────────────────
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  // ── Drag / Pan ────────────────────────────────────────────────────────────
  onMouseDown(event: MouseEvent): void {
    if (!this.isZoomed()) return;
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.lastTranslateX = this.translateX();
    this.lastTranslateY = this.translateY();
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;
    this.translateX.set(this.lastTranslateX + dx);
    this.translateY.set(this.lastTranslateY + dy);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  // ── Acciones ─────────────────────────────────────────────────────────────
  zoomIn(): void {
    if (this.canZoomIn()) this.scale.update(s => +(s + 0.25).toFixed(2));
  }

  zoomOut(): void {
    if (this.canZoomOut()) {
      this.scale.update(s => +(s - 0.25).toFixed(2));
      // Si el zoom vuelve al base, resetear el paneo
      if (this.scale() <= 1) this.resetTransform();
    }
  }

  resetTransform(): void {
    this.scale.set(1);
    this.translateX.set(0);
    this.translateY.set(0);
  }

  downloadImage(): void {
    const url = this.config()?.url;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = this.config()?.title || 'imagen';
    link.target = '_blank';
    link.click();
  }

  close(): void {
    this.resetTransform();
    this.viewerService.close();
  }

  ngOnDestroy(): void {
    this.isDragging = false;
    popDialog(this.stackId);
    this.host.nativeElement.remove();
  }
}
